import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import ErrorBoundary from "@/components/ErrorBoundary";
import RequireRole from "@/components/RequireRole";

import { ThemeProvider } from "@/lib/theme";
import { SiteProvider } from "@/lib/site";
import { AuthProvider } from "@/lib/auth";

// Public Marketing Pages (Lazy Loaded)
const AISolutions = lazy(() => import("@/pages/AISolutions"));
const SoftwareEngineering = lazy(() => import("@/pages/SoftwareEngineering"));
const StaffAugmentation = lazy(() => import("@/pages/StaffAugmentation"));
const IndustriesPage = lazy(() => import("@/pages/IndustriesPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const InsightDetail = lazy(() => import("@/pages/InsightDetail"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Admin Shell & Pages (Lazy Loaded)
const AdminShell = lazy(() => import("@/pages/admin/AdminShell"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const ResourcePage = lazy(() => import("@/pages/admin/ResourcePage"));
const LeadsView = lazy(() => import("@/pages/admin/InboxViews").then(module => ({ default: module.LeadsView })));
const BookingsView = lazy(() => import("@/pages/admin/InboxViews").then(module => ({ default: module.BookingsView })));
const JobApplicationsPage = lazy(() => import("@/pages/admin/JobApplicationsPage"));
const TalentPoolPage = lazy(() => import("@/pages/admin/TalentPoolPage"));
const SiteSettings = lazy(() => import("@/pages/admin/SiteSettings"));
const MediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const UsersAdmin = lazy(() => import("@/pages/admin/UsersAdmin"));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Minimal fallback component during code fetch
function PageLoader() {
  return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
  );
}

function LegacyPrefixRedirect() {
  const { "*": legacyPath = "" } = useParams();
  const { search, hash } = useLocation();
  const pathname = legacyPath ? `/${legacyPath}` : "/";
  return <Navigate to={{ pathname, search, hash }} replace />;
}

function LegacyJobRedirect({ apply = false }) {
  const { legacyValue = "" } = useParams();
  const safeValue = encodeURIComponent(legacyValue);
  const target = apply ? `/careers?apply=${safeValue}` : `/careers/${safeValue}`;
  return <Navigate to={target} replace />;
}

function App() {
  return (
      <ThemeProvider>
        <AuthProvider>
          <SiteProvider>
            <BrowserRouter basename={process.env.PUBLIC_URL}>
              <ScrollToTop />
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route element={<Layout />}>
                      <Route index element={<HomePage />} />
                      <Route path="/ai-solutions" element={<AISolutions />} />
                      <Route path="/software-engineering" element={<SoftwareEngineering />} />
                      <Route path="/staff-augmentation" element={<StaffAugmentation />} />
                      <Route path="/industries" element={<IndustriesPage />} />
                      <Route path="/case-studies" element={<CaseStudiesPage />} />
                      <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
                      <Route path="/insights" element={<InsightsPage />} />
                      <Route path="/insights/:slug" element={<InsightDetail />} />
                      <Route path="/solutions/:slug" element={<LandingPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/careers" element={<CareersPage />} />
                      <Route path="/careers/:jobSlug" element={<CareersPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/faq" element={<FAQPage />} />

                      {/* Permanent Nginx redirects handle these in production.
                          Client routes keep local/dev and stale cached SPA shells safe. */}
                      <Route path="/miaki-frontend" element={<Navigate to="/" replace />} />
                      <Route path="/miaki-frontend/*" element={<LegacyPrefixRedirect />} />
                      <Route path="/miaki_career" element={<Navigate to="/careers" replace />} />
                      <Route path="/index.php/miaki_career" element={<Navigate to="/careers" replace />} />
                      <Route path="/about_us" element={<Navigate to="/about" replace />} />
                      <Route path="/index.php/about_us" element={<Navigate to="/about" replace />} />
                      <Route path="/blog" element={<Navigate to="/insights" replace />} />
                      <Route path="/index.php/blog" element={<Navigate to="/insights" replace />} />
                      <Route path="/services" element={<Navigate to="/software-engineering" replace />} />
                      <Route path="/index.php/services" element={<Navigate to="/software-engineering" replace />} />
                      <Route path="/quote" element={<Navigate to="/contact" replace />} />
                      <Route path="/index.php/quote" element={<Navigate to="/contact" replace />} />
                      <Route path="/consultant" element={<Navigate to="/contact" replace />} />
                      <Route path="/index.php/consultant" element={<Navigate to="/contact" replace />} />
                      <Route path="/portfolio" element={<Navigate to="/case-studies" replace />} />
                      <Route path="/index.php/portfolio" element={<Navigate to="/case-studies" replace />} />
                      <Route path="/job_description/:legacyValue" element={<LegacyJobRedirect />} />
                      <Route path="/index.php/job_description/:legacyValue" element={<LegacyJobRedirect />} />
                      <Route path="/career_form/:legacyValue" element={<LegacyJobRedirect apply />} />
                      <Route path="/index.php/career_form/:legacyValue" element={<LegacyJobRedirect apply />} />

                      {/* Admin shell route */}
                      <Route path="/admin/*" element={<AdminShell />}>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="callback" element={<Navigate to="/admin/dashboard" replace />} />
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route
                            path="leads"
                            element={<RequireRole minRole="admin"><LeadsView /></RequireRole>}
                        />
                        <Route
                            path="bookings"
                            element={<RequireRole minRole="admin"><BookingsView /></RequireRole>}
                        />
                        <Route
                            path="job-applications"
                            element={<RequireRole minRole="admin"><JobApplicationsPage /></RequireRole>}
                        />
                        <Route
                            path="talent-pool"
                            element={<RequireRole minRole="admin"><TalentPoolPage /></RequireRole>}
                        />
                        <Route path="content/:resource" element={<ResourcePage />} />
                        <Route path="site" element={<SiteSettings />} />
                        <Route path="media" element={<MediaLibrary />} />
                        <Route path="users" element={<UsersAdmin />} />
                        <Route path="audit" element={<AuditLog />} />
                      </Route>

                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
          </SiteProvider>
        </AuthProvider>
      </ThemeProvider>
  );
}

export default App;
